import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { GET_USER_DETAILS_QUERY_KEY } from '../../constants/queryKeys';

import { CandidateProfileFormData } from '../../models';
import { useAuth } from '../profile/logic/auth';
import CandidateProfileForm from './components/CandidateProfileForm';
import {
  CreateCandidateFormLocationState,
  useCreateCandidateForm,
} from './logic/useCreateCandidateForm';
import { useVerifyCandidateProfile } from './logic/useVerifyCandidateProfile';
import RouteKey from '../../routes/key';

const { auth: authUrl } = RouteKey.child;
const { successfulAccountCreation } = RouteKey.auth.child;

const getIsDisabledInputs = (state: any, disableInputsSingPass: any, disableInputsManual: any) =>
  state?.credentialType === 'SINGPASS' && state?.hasMyInfoProfile
    ? disableInputsSingPass
    : disableInputsManual;

const CreateCandidateForm = () => {
  const history = useHistory();
  const auth = useAuth();
  const [profileData, setProfileData] = useState<CandidateProfileFormData>();
  const disableInputsManual = {
    hanyupinyinName: true,
    gender: true,
    race: true,
    citizenship: true,
    nationality: true,
    nationalityIssueDate: true,
    prStatus: true,
    occupation: true,
    email: true,
    reEnterEmail: true,
    mobileCountryCode: true,
    mobileContactNumber: true,
    preferredTelephoneOrMobile: true,
    telephoneCountryCode: true,
    telephoneContactNumber: true,
    houseOrBlkNo: true,
    streetName: true,
    building: true,
    floor: true,
    unit: true,
    postalCode: true,
    sameAsRegisteredAddress: true,
    preferredAddress: true,
    foreignAddress1: true,
    foreignAddress2: true,
    foreignAddress3: true,
    nextOfKinName: true,
    nextOfKinRelationshipToCandidate: true,
    nextOfKinCountryCode: true,
    nextOfKinContactNumber: true,
  };

  const { state } = useLocation<CreateCandidateFormLocationState>();
  const disableInputsSingPass = {
    ...disableInputsManual,
    name: state?.formData?.formDataIsDisabled?.name,
    IdType: state?.formData?.formDataIsDisabled?.IdType,
    NricFin: state?.formData?.formDataIsDisabled?.NricFin,
    dateOfBirth: state?.formData?.formDataIsDisabled?.dateOfBirth,
    hanyupinyinName: state?.formData?.formDataIsDisabled?.hanyupinyinName,
    prStatus: state?.formData?.formDataIsDisabled?.prStatus,
  };

  const isDisableInputs = getIsDisabledInputs(state, disableInputsSingPass, disableInputsManual);
  const queryClient = useQueryClient();

  const { createCandidate, submitError, createCandidateMutation } = useCreateCandidateForm(() => {
    if (state?.credentialType === 'SINGPASS') {
      // call get user details API to update login status because create candidate API will return new login cookies
      queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
      auth.signin();
    }
    history.push(`/${authUrl}/${successfulAccountCreation}`, {
      credentialType: state?.credentialType,
    });
  });

  const handleVerifyCandidate = (formData: CandidateProfileFormData) => {
    verifyCandidate({
      ...formData,
      name: state?.formData?.formDataDefaultValues?.name || formData.name,
      NricFin: state?.formData?.formDataDefaultValues?.NricFin || formData.NricFin,
      dateOfBirth: formData.dateOfBirth,
    });
  };

  const handleSubmit = (formData: CandidateProfileFormData) => {
    let birthDate;
    if (profileData?.dateOfBirth) {
      birthDate = profileData.dateOfBirth;
    }
    createCandidate(
      {
        ...formData,
        dateOfBirth: (birthDate || formData.dateOfBirth),
        nationalityIssueDate: formData.nationalityIssueDate,
        secondaryRace: state?.formData?.formDataDefaultValues.secondaryRace ?? '',
        name: profileData?.name,
        NricFin: profileData?.NricFin,
        IdType: profileData?.IdType,
        school: profileData?.school,
      },
      state?.credentialType
    );
  };

  const {
    mandatorySingpass,
    optionalSingpass,
    verifyCandidate,
    verifyError,
    verifyCandidateMutation,
  } = useVerifyCandidateProfile(() => {
    // do nothing
  });

  return (
    <CandidateProfileForm
      onCallVerifyCandidate={handleVerifyCandidate}
      onCallHandleSubmit={handleSubmit}
      formDefaultValues={state?.formData?.formDataDefaultValues}
      isDisableInputs={isDisableInputs}
      isDisableValidationForDisabledInputs
      verifyCandidateSubmitError={verifyError}
      isSingPassUser={state?.credentialType === 'SINGPASS' && state?.hasMyInfoProfile}
      profileData={profileData}
      setProfileData={setProfileData}
      verifyCandidateStatus={verifyCandidateMutation.status}
      createAccountStatus={createCandidateMutation.status}
      mandatorySingpass={mandatorySingpass}
      optionalSingpass={optionalSingpass}
      createCandidateError={submitError}
    />
  );
};

export default CreateCandidateForm;
